import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AppShell from "../../components/AppShell";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Mail, MessageCircle } from "lucide-react";
import { PRIORITIES, STATUSES, PriorityBadge, StatusBadge, fmtDate, btn } from "../../components/leads/PriorityBadge";
import { SectionTabs } from "../../components/leads/SectionTabs";

const Row = ({ label, value }) => (value || value === 0) ? (
  <div style={{ display: "grid", gridTemplateColumns: "minmax(120px, 170px) 1fr", gap: 12, padding: "9px 0", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
    <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "#737373" }}>{label}</div>
    <div style={{ fontSize: 14, color: "#e5e5e5", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{value}</div>
  </div>
) : null;

const Card = ({ title, children, testid }) => (
  <div data-testid={testid} style={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 4, padding: 18, marginBottom: 16 }}>
    <div style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: "#E11D2E", fontWeight: 700, marginBottom: 8 }}>{title}</div>
    {children}
  </div>
);

const Answers = ({ l }) => (<>
  <Row label="Rellena" value={l.filled_by === "guardian" ? "Padre/madre/tutor" : "Futbolista"} />
  <Row label="Futbolista" value={l.player_name} />
  <Row label="Adulto responsable" value={l.guardian_name} />
  <Row label="Edad" value={`${l.age} años${l.age < 18 ? " (menor)" : ""}`} />
  <Row label="Posición" value={(l.positions || []).join(" · ")} />
  <Row label="Objetivo principal" value={(l.goals || []).join(" · ")} />
  <Row label="Detalle objetivo" value={l.goals_detail} />
  <Row label="Experiencia previa / situación" value={l.current_situation || "Sin respuesta"} />
  <Row label="Principal obstáculo" value={l.obstacle} />
  <Row label="Presupuesto mensual" value={l.budget_label} />
  <Row label="Quiere empezar" value={l.start_when_label} />
  <Row label="Teléfono / WhatsApp" value={l.phone} />
  <Row label="Email" value={l.email} />
</>);

const StatusEditor = ({ l, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const save = async (st) => {
    setSaving(true);
    try { onSaved((await api.patch(`/admin/captacion/${l.id}/status`, { status: st })).data); toast.success("Estado actualizado"); }
    catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  };
  return (
    <Card title="Estado" testid="lead-status-card">
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {Object.entries(STATUSES).map(([k, c]) => {
          const on = (l.status || "new") === k;
          return (
            <button key={k} disabled={saving || on} onClick={() => save(k)} data-testid={`lead-set-status-${k}`}
              style={{ ...btn(on ? "rgba(255,255,255,0.06)" : "transparent"), color: c.fg, border: `1px solid ${on ? c.fg : "rgba(255,255,255,0.14)"}` }}>
              {c.label}
            </button>
          );
        })}
      </div>
    </Card>
  );
};

const PriorityEditor = ({ l, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const save = async (p) => {
    if (p === l.priority) return;
    setSaving(true);
    try { onSaved((await api.patch(`/admin/captacion/${l.id}/priority`, { priority: p })).data); toast.success("Prioridad actualizada"); }
    catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  };
  return (
    <Card title="Prioridad" testid="lead-priority-card">
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {Object.entries(PRIORITIES).map(([k, c]) => (
          <button key={k} disabled={saving} onClick={() => save(k)} data-testid={`lead-set-priority-${k}`}
            style={{ ...btn(l.priority === k ? c.bg : "transparent"), color: c.fg, border: `1px solid ${l.priority === k ? c.fg : "rgba(255,255,255,0.14)"}`, minWidth: 84, justifyContent: "center" }}>
            {c.label}
          </button>
        ))}
      </div>
      <div style={{ fontSize: 12, color: "#a3a3a3", marginTop: 12, lineHeight: 1.6 }} data-testid="lead-priority-info">
        Sugerida automáticamente: <b style={{ color: PRIORITIES[l.priority_auto]?.fg }}>{PRIORITIES[l.priority_auto]?.label}</b>
        {l.priority_source === "manual" && " · Cambiada a mano por ti"}
        {(l.priority_reasons || []).length > 0 && <div>Motivo: {l.priority_reasons.join(" · ")}</div>}
        <div style={{ color: "#737373" }}>Es solo una ayuda. Si el lead reenvía el formulario se vuelve a calcular.</div>
      </div>
    </Card>
  );
};

export default function AdminLeadDetail() {
  const { id } = useParams();
  const [l, setL] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/admin/captacion/${id}`).then((r) => setL(r.data)).catch((e) => setErr(formatErr(e))); }, [id]);
  const wa = (l?.phone || "").replace(/\D/g, "");

  return (
    <AppShell>
      <SectionTabs active="leads" />
      <Link to="/app/admin/leads" style={{ ...btn("transparent"), marginBottom: 18 }} data-testid="lead-back"><ArrowLeft size={14} /> Leads</Link>
      {err && <div style={{ color: "#f87171" }} data-testid="lead-error">{err}</div>}
      {!l && !err && <div style={{ color: "#a3a3a3" }}>Cargando…</div>}
      {l && (<div data-testid="lead-detail">
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 6 }}>
          <h1 style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: "2.2rem", letterSpacing: "0.04em", margin: 0 }} data-testid="lead-name">{l.player_name}</h1>
          <span style={{ fontSize: 16, color: "#a3a3a3" }} data-testid="lead-age">{l.age} años</span>
          <PriorityBadge p={l.priority} testid="lead-detail-priority" />
          <StatusBadge s={l.status} testid="lead-detail-status" />
        </div>
        <div style={{ fontSize: 13, color: "#a3a3a3", marginBottom: 16 }} data-testid="lead-dates">
          Entrada {fmtDate(l.created_at)}{l.submission_count > 1 && ` · Último envío ${fmtDate(l.last_submitted_at)} · ${l.submission_count} envíos`}
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
          <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" style={btn("#25D366")} data-testid="lead-whatsapp"><MessageCircle size={14} /> Abrir WhatsApp</a>
          <a href={`mailto:${l.email}`} style={btn("transparent")} data-testid="lead-email"><Mail size={14} /> {l.email}</a>
        </div>
        <StatusEditor l={l} onSaved={(r) => setL({ ...l, status: r.status })} />
        <PriorityEditor l={l} onSaved={(r) => setL({ ...l, ...r })} />
        <Card title="Respuestas" testid="lead-answers"><Answers l={l} /></Card>
        <Card title="Consentimiento" testid="lead-consent">
          <Row label="Dado por" value={l.consent?.given_by === "guardian" ? "Padre/madre/tutor" : "Futbolista"} />
          <Row label="Fecha" value={fmtDate(l.consent?.accepted_at)} />
          <Row label="Versión política" value={l.consent?.privacy_version} />
        </Card>
        {(l.history || []).length > 0 && (
          <Card title={`Envíos anteriores (${l.history.length})`} testid="lead-history">
            {[...l.history].reverse().map((h, i) => (
              <details key={i} style={{ borderTop: "1px solid rgba(255,255,255,0.05)", padding: "10px 0" }} data-testid={`lead-history-${i}`}>
                <summary style={{ cursor: "pointer", fontSize: 13, color: "#e5e5e5" }}>
                  {fmtDate(h.submitted_at)} · {h.budget_label}{h.priority_auto && ` · sugerida ${PRIORITIES[h.priority_auto]?.label}`}
                </summary>
                <div style={{ marginTop: 8 }}><Answers l={h} /></div>
              </details>
            ))}
          </Card>
        )}
      </div>)}
    </AppShell>
  );
}
